// A station's session, on the Floor's station modal (spec/13 §3.3, F-SES-04/-14/-16/-17/-18/-21): the Session tab
// — the player, what was charged or is due, what pays for the time left, Move and End, and the timeline — and the
// forms the modal's action bar opens: Start session, Sell time and Add free time. The modal's header owns the clock and
// its bar owns Sell time, Add free time, Message and Lock, so the tab does not repeat them. It reads the session and its
// timeline through the generated client (`queries.ts`); an action re-reads both, so the tab stays live.
//
// *Until 2026-10-07 this was also the Sessions screen's sheet; the Sessions screen folded into the Floor, and with it
// the sheet's own copies of the bar's actions — which locked a staff-run session at the station and started a console
// on a PC's form — went.*
//
// Start session (F-SES-04, spec/05 §3.1): staff pick the player and fund it as pay as you go (a budget on account) or
// free time; the server funds from the player's passes first either way. An action opens a short inline form in a card,
// never a modal on a modal. Ending a session cannot be undone, so it confirms.

import type {
  OrganizationPlayer,
  Session,
  SessionEvent,
  SessionMoveWarning,
  SessionSegment,
  SessionTimelineOffline,
  SessionTimelinePlay,
} from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Checkbox, EmptyState, SegmentedControl, SelectField, Skeleton, TextField, useToast } from '@clavian/ui-web';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent, type ReactNode } from 'react';

import { useApi } from '../../api/client';
import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { useFloor } from '../../floor/queries';
import { secondsLeftNow, useNow } from '../../floor/live';
import { Icon } from '../../shell/nav-icons';
import { useConsoleScope, useScopeIds } from '../../shell/scope';
import { Facts, Notice, Section } from '../../ui';
import { majorToMinor } from '../money';
import { playerName } from '../players/queries';
import { bpsToPercent } from '../pricing/terms';
import { breakdownAtRemaining } from '@clavian/domain/funding-breakdown';

import { formatDuration, formatMoney } from './money';
import { SessionLedger } from './SessionLedger';
import { SessionPlayer } from './SessionPlayer';
import { PlayerPicker } from './PlayerPicker';
import { ConsoleDue } from './ConsoleSession';
import {
  moveCandidates,
  useCompSession,
  useEndSession,
  useMoveSession,
  useSellSessionTime,
  useSession,
  useSessionTimeline,
  useStartSession,
  useStationSession,
} from './queries';

import './sessions.css';

const SESSIONS_END = 'sessions:end';
const SESSIONS_MOVE = 'sessions:move';
const PLAYERS_READ = 'players:read';

/** The session on a station — the Session tab — or its free state. */
export function StationSessionPanel({
  organizationId,
  venueId,
  stationId,
  can,
  freeHint,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly stationId: string;
  readonly can: (permission: string) => boolean;
  /** What a free station says under "Free". */
  readonly freeHint?: ReactNode;
}): ReactNode {
  const { t } = useTranslation('console');
  const query = useStationSession(organizationId, venueId, stationId, true);

  if (query.data === undefined) {
    return query.isError ? (
      <Notice tone="danger" role="alert">
        {t('sessions.drawer.unavailable')}
      </Notice>
    ) : (
      <div className="console-session" aria-busy="true" role="status" aria-label={t('sessions.drawer.loading')}>
        <Skeleton height="8rem" />
        <Skeleton shape="text" lines={3} />
      </div>
    );
  }
  if (query.data === null) {
    // A free station: "Free" — the modal's bar offers Start session.
    return (
      <div className="console-session">
        <EmptyState size="compact" icon={<Icon name="timer" />} title={t('sessions.drawer.freeTitle')} description={freeHint ?? t('sessions.drawer.free')} />
      </div>
    );
  }
  return <SessionBody organizationId={organizationId} initial={query.data} readAt={query.dataUpdatedAt} can={can} />;
}

type StartFunding = 'postpaid' | 'comp';

/**
 * Start a session on a free station (F-SES-04, spec/05 §3.1): find the player, then fund it — pay as
 * you go with a budget on account (typed in the venue's currency, sent as integer minor units), or comp
 * minutes with a reason. The server plays the player's passes first either way (spec/05 §4.1).
 */
export function StartSessionForm({
  organizationId,
  venueId,
  stationId,
  canComp,
  pickGame = false,
  onDone,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly stationId: string;
  readonly canComp: boolean;
  /**
   * A staff-run station (ADR-0033): staff may say which venue-library title is played, so Games played and the
   * player's history count it. Needs `library:read`, which the caller checked.
   */
  readonly pickGame?: boolean;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const api = useApi();
  const { currency } = useScopeIds();
  const start = useStartSession(organizationId, venueId);

  const [player, setPlayer] = useState<OrganizationPlayer | null>(null);
  const [funding, setFunding] = useState<StartFunding>('postpaid');
  const [budget, setBudget] = useState('');
  const [minutes, setMinutes] = useState('');
  const [reason, setReason] = useState<(typeof COMP_REASONS)[number]>('compensation');
  const [note, setNote] = useState('');
  // "Use wallet, save my pass" (F-SES-09, spec/05 §4.2): the player's wallet is drawn before their passes.
  const [walletFirst, setWalletFirst] = useState(false);
  // The title played on a staff-run station (ADR-0033); empty is "console play", not tracked.
  const [game, setGame] = useState('');
  const library = useQuery({
    queryKey: ['sessions', 'start-games', organizationId, venueId],
    queryFn: () => api.listLibraryEntries({ path: { organizationId, venueId }, query: { limit: 100 } }),
    enabled: pickGame,
    staleTime: 60_000,
  });
  const gameOptions = [
    { value: '', label: t('sessions.start.gameNone') },
    ...(library.data?.data ?? [])
      .filter((entry) => entry.visible)
      .map((entry) => ({ value: entry.id, label: entry.game?.name ?? entry.customApp?.name ?? entry.id })),
  ];
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  /** The catalogue's words for the code; a player already on another station is said in the staff's terms. */
  function refusal(mutationError: unknown): string {
    if (problemCode(mutationError) === PROBLEM.alreadyInSession) return t('sessions.start.alreadyInSession');
    return problem.text(mutationError, { fallback: t('sessions.start.refused') });
  }

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(undefined);
    setFieldError(undefined);
    if (player === null) {
      setError(t('sessions.start.pickPlayer'));
      return;
    }
    let body;
    if (funding === 'comp') {
      const value = Number(minutes);
      if (minutes.trim() === '' || !Number.isInteger(value) || value < 1) {
        setFieldError(t('sessions.start.minutesRequired'));
        return;
      }
      // The reason is one of the server's list (F-SES-16); `other` carries the note saying why.
      if (reason === 'other' && note.trim() === '') {
        setFieldError(t('sessions.comp.noteRequired'));
        return;
      }
      body = { stationId, playerId: player.id, funding: { kind: 'comp' as const, minutes: value, reason, ...(note.trim() === '' ? {} : { note: note.trim() }) } };
    } else {
      if (currency === undefined) {
        setError(t('sessions.start.noCurrency'));
        return;
      }
      // Integer minor units, parsed from the typed string without a float (spec/06 §1).
      const amount = majorToMinor(budget, currency);
      if (amount === null) {
        setFieldError(t('sessions.start.budgetInvalid'));
        return;
      }
      body = { stationId, playerId: player.id, funding: { kind: 'postpaid' as const, budgetAmount: { amount, currency } } };
    }
    const name = playerName(player);
    const withGame = pickGame && game !== '' ? { ...body, libraryEntryId: game } : body;
    start.mutate(walletFirst ? { ...withGame, prefer: 'wallet' as const } : withGame, {
      onSuccess: () => {
        show({ tone: 'ok', title: t('sessions.start.done', { player: name }) });
        onDone();
      },
      onError: (mutationError) => setError(refusal(mutationError)),
    });
  }

  return (
    <form className="console-session__form" aria-label={t('sessions.start.title')} noValidate onSubmit={submit}>
      {error !== undefined ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}

      <PlayerPicker organizationId={organizationId} player={player} onPick={setPlayer} />

      {canComp ? (
        <SegmentedControl<StartFunding>
          label={t('sessions.start.funding')}
          value={funding}
          onValueChange={(next) => {
            setFunding(next);
            setFieldError(undefined);
          }}
          block
          options={[
            { value: 'postpaid', label: t('sessions.start.postpaid') },
            { value: 'comp', label: t('sessions.start.comp') },
          ]}
        />
      ) : null}

      {funding === 'postpaid' ? (
        <TextField
          label={t('sessions.start.budget', { currency: currency ?? '' })}
          description={t('sessions.start.budgetHint')}
          required
          numeric
          inputMode="decimal"
          value={budget}
          onChange={(e) => setBudget(e.target.value)}
          error={fieldError}
        />
      ) : (
        <>
          <MinuteChips value={minutes} onChange={setMinutes} label={t('sessions.start.minutes')} />
          <TextField label={t('sessions.start.minutes')} description={t('sessions.start.minutesHint')} required numeric inputMode="numeric" value={minutes} onChange={(e) => setMinutes(e.target.value)} error={fieldError} />
          <SelectField label={t('sessions.start.reason')} required options={COMP_REASONS.map((r) => ({ value: r, label: t(`sessions.reason.${r}`) }))} value={reason} onValueChange={(v) => setReason(v as (typeof COMP_REASONS)[number])} />
          {reason === 'other' ? <TextField label={t('sessions.comp.note')} description={t('sessions.comp.noteHint')} required value={note} onChange={(e) => setNote(e.target.value)} /> : null}
        </>
      )}

      {pickGame ? (
        <SelectField label={t('sessions.start.game')} description={t('sessions.start.gameHint')} options={gameOptions} value={game} onValueChange={setGame} />
      ) : null}

      <Checkbox
        label={t('sessions.start.walletFirst')}
        checked={walletFirst}
        onCheckedChange={(checked) => setWalletFirst(checked === true)}
      />

      <FormActions busy={start.isPending} submitLabel={t('sessions.start.submit')} onCancel={onDone} />
    </form>
  );
}

/** The live view of one session: Move and End, the player and charges, funding and the timeline. */
function SessionBody({
  organizationId,
  initial,
  readAt,
  can,
}: {
  readonly organizationId: string;
  readonly initial: Session;
  readonly readAt: number;
  readonly can: (permission: string) => boolean;
}): ReactNode {
  const { t } = useTranslation('console');
  const live = useSession(organizationId, initial.id);
  const session = live.data ?? initial;
  const now = useNow();
  // The time left, counted down here as the modal's header counts it, for what pays for it (spec/05 §4.4).
  const left = secondsLeftNow(session, live.data === undefined ? readAt : live.dataUpdatedAt, now);
  const timeline = useSessionTimeline(organizationId, initial.id);
  const ended = session.status === 'ended';

  const sourceLabel = (source: string): string => t(`sessions.source.${source}`, { defaultValue: source });
  const started = new Date(session.startedAt).getTime();
  const elapsed = Number.isNaN(started) ? 0 : Math.max(0, (now - started) / 1000);

  return (
    <div className="console-session">
      {ended ? null : <Actions organizationId={organizationId} session={session} can={can} />}

      <Facts
        columns={2}
        items={[
          {
            key: 'player',
            label: t('sessions.field.player'),
            value:
              session.walkIn === true ? (
                t('consoleSession.walkIn')
              ) : session.guest !== undefined ? (
                // A guest (F-SES-07): no player profile, the ticket code instead.
                t('consoleSession.guest', { code: session.guest.code })
              ) : (
                <SessionPlayer organizationId={organizationId} playerId={session.playerId} canReadPlayers={can(PLAYERS_READ)} />
              ),
          },
          // A booked console session (ADR-0034) is charged nothing as it runs: it shows what is due at the end.
          session.booking === undefined
            ? { key: 'charged', label: t('sessions.field.charged'), value: <span className="console-money">{formatMoney(session.charged.chargedAmount)}</span> }
            : { key: 'due', label: t('consoleSession.preview.due'), value: <span className="console-money">{formatMoney(session.booking.quote.total)}</span> },
          { key: 'started', label: t('sessions.field.started'), value: <span className="console-num">{formatClock(session.startedAt)}</span> },
          { key: 'played', label: t('sessions.field.played'), value: <span className="console-num">{formatDuration(elapsed)}</span> },
          // Offline play (spec/05 §8.5): the minutes the PC ran it while it could not reach Clavian, settled on reconnect.
          ...(session.offlineSeconds !== undefined && session.offlineSeconds > 0
            ? [{ key: 'offline', label: t('sessions.field.offline'), value: <span className="console-num">{t('sessions.field.playedOffline', { duration: formatDuration(session.offlineSeconds) })}</span> }]
            : []),
        ]}
      />

      {session.booking !== undefined ? (
        <Section title={t('consoleSession.due.title')} titleAs="h3">
          <ConsoleDue session={session} />
        </Section>
      ) : (
      <Section title={t('sessions.plan.title')} titleAs="h3" body="rows">
        {/* What the time left is made of, in the order it is used (spec/05 §4.4): the same breakdown the player's
            kiosk shows, counted down against this drawer's clock. An older api sends none: the plan alone. */}
        {!ended && session.funding !== undefined ? (
          <FundingRows funding={session.funding} left={left} />
        ) : session.plan.length === 0 ? (
          <p className="console-session__empty">{t('sessions.plan.empty')}</p>
        ) : (
          <ul className="console-session__plan">
            {session.plan.map((segment, index) => (
              <li key={`${segment.source}-${index}`} className="console-session__plan-row">
                <span>{segment.label ?? sourceLabel(segment.source)}</span>
                <span className="console-money">
                  {index === 0 ? t('sessions.plan.now') : t('sessions.plan.queued', { duration: formatDuration(segment.seconds) })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
      )}

      <Timeline
        loading={timeline.data === undefined && !timeline.isError}
        segments={timeline.data?.segments ?? []}
        events={timeline.data?.events ?? []}
        plays={timeline.data?.plays ?? []}
        offline={timeline.data?.offline ?? []}
        sourceLabel={sourceLabel}
      >
        {/* The journal entries, newest first and paged (F-SES-21 rule 6): read on their own, never whole. */}
        <SessionLedger organizationId={organizationId} sessionId={session.id} />
      </Timeline>
    </div>
  );
}

/**
 * The session's funding breakdown (spec/05 §4.4): each source of the time left, in the order the session uses it —
 * comp, the passes (with when each stops counting), the wallet and time on account (with what the time costs at the
 * rate) — then grace or the end. `left` is the drawer's own time left; the breakdown is counted down to it.
 */
function FundingRows({ funding, left }: { readonly funding: NonNullable<Session['funding']>; readonly left: number }): ReactNode {
  const { t } = useTranslation('console');
  const live = breakdownAtRemaining(funding, left);
  const detailOf = (item: (typeof live.items)[number]): string | null => {
    if (item.kind === 'pass') {
      if (item.windowClosesAt !== null) return t('sessions.plan.passUntil', { time: formatClock(item.windowClosesAt) });
      if (item.expiresAt !== null) return t('sessions.plan.passExpires', { when: formatDay(item.expiresAt) });
      return null;
    }
    if ((item.kind === 'wallet' || item.kind === 'postpaid') && item.amount !== null && item.rate !== null) {
      return t('sessions.plan.costAtRate', { amount: formatMoney(item.amount), rate: formatMoney(item.rate.hourlyAmount) });
    }
    return null;
  };
  return (
    <ul className="console-session__plan">
      {live.items.map((item, index) => {
        const detail = detailOf(item);
        return (
          <li key={`${item.kind}-${item.holdingId ?? index}`} className="console-session__plan-row">
            <span>
              {item.kind === 'pass' ? (item.name ?? t('sessions.source.pass')) : t(`sessions.plan.kind.${item.kind}`)}
              {index === 0 ? <span className="console-subtle"> · {t('sessions.plan.now')}</span> : null}
              {detail === null ? null : <span className="console-session__plan-detail">{detail}</span>}
            </span>
            <span className="console-money">{formatDuration(item.seconds)}</span>
          </li>
        );
      })}
      <li className="console-session__plan-row console-subtle">
        <span>{live.then.kind === 'grace' ? t('sessions.plan.thenGrace', { minutes: Math.max(1, Math.round(live.then.seconds / 60)) }) : t('sessions.plan.thenEnd')}</span>
      </li>
    </ul>
  );
}

type OpenForm = 'move' | 'end' | null;

/**
 * The Session tab's own actions (spec/13 §3.3): Move and End. Sell time, Add free time, Message and Lock are the
 * modal's action bar's. Only the permitted ones are offered.
 */
function Actions({ organizationId, session, can }: { readonly organizationId: string; readonly session: Session; readonly can: (permission: string) => boolean }): ReactNode {
  const { t } = useTranslation('console');
  const [open, setOpen] = useState<OpenForm>(null);
  // A booked console session (ADR-0034) places its one order as it ends: End says so.
  const booked = session.booking !== undefined;
  const canMove = can(SESSIONS_MOVE);
  const canEnd = can(SESSIONS_END);
  if (!canMove && !canEnd) return null;

  const toggle = (form: Exclude<OpenForm, null>) => setOpen(open === form ? null : form);

  return (
    <div className="console-session__actions-block">
      <div className="console-session__actions" role="group" aria-label={t('sessions.actions.title')}>
        {canMove ? (
          <Button variant="secondary" size="sm" icon={<Icon name="grid" />} aria-expanded={open === 'move'} onClick={() => toggle('move')}>
            {t('sessions.actions.move')}
          </Button>
        ) : null}
        {canEnd ? (
          <Button variant="danger" size="sm" icon={<Icon name="stop" />} aria-expanded={open === 'end'} onClick={() => toggle('end')}>
            {t('sessions.actions.end')}
          </Button>
        ) : null}
      </div>
      {open === 'move' ? <MoveForm organizationId={organizationId} session={session} onDone={() => setOpen(null)} /> : null}
      {open === 'end' ? <EndForm organizationId={organizationId} sessionId={session.id} booked={booked} onDone={() => setOpen(null)} /> : null}
    </div>
  );
}

const COMP_REASONS = ['compensation', 'promo', 'staff_play', 'other'] as const;
/** One tap for the usual amounts; any other number can still be typed. */
const QUICK_MINUTES = ['15', '30', '60'] as const;

function MinuteChips({ value, onChange, label }: { readonly value: string; readonly onChange: (value: string) => void; readonly label: string }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <div className="console-session__quick" role="group" aria-label={label}>
      {QUICK_MINUTES.map((minutes) => (
        <button key={minutes} type="button" className="console-session__quick-chip" aria-pressed={value === minutes} onClick={() => onChange(minutes)}>
          {t('sessions.quickMinutes', { n: Number(minutes) })}
        </button>
      ))}
    </div>
  );
}

export function CompForm({ organizationId, sessionId, onDone }: FormProps): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const comp = useCompSession(organizationId, sessionId);
  const [minutes, setMinutes] = useState('');
  const [reason, setReason] = useState<(typeof COMP_REASONS)[number]>('compensation');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const value = Number(minutes);
    if (minutes.trim() === '' || !Number.isInteger(value) || value < 1) {
      setError(t('sessions.comp.required'));
      return;
    }
    if (reason === 'other' && note.trim() === '') {
      setError(t('sessions.comp.noteRequired'));
      return;
    }
    setError(undefined);
    comp.mutate(
      { minutes: value, reason, ...(note.trim() === '' ? {} : { note: note.trim() }) },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('sessions.comp.done', { minutes: value }) });
          onDone();
        },
        onError: (mutationError) =>
          setError(problemCode(mutationError) === PROBLEM.conflict ? t('sessions.comp.limit') : problem.text(mutationError, { fallback: t('sessions.comp.refused') })),
      },
    );
  }

  return (
    <form className="console-session__form" aria-label={t('sessions.comp.title')} noValidate onSubmit={submit}>
      <MinuteChips value={minutes} onChange={setMinutes} label={t('sessions.comp.minutes')} />
      <TextField label={t('sessions.comp.minutes')} description={t('sessions.comp.minutesHint')} required numeric inputMode="numeric" value={minutes} onChange={(e) => setMinutes(e.target.value)} error={error} />
      <SelectField label={t('sessions.comp.reason')} required options={COMP_REASONS.map((r) => ({ value: r, label: t(`sessions.reason.${r}`) }))} value={reason} onValueChange={(v) => setReason(v as (typeof COMP_REASONS)[number])} />
      {reason === 'other' ? <TextField label={t('sessions.comp.note')} description={t('sessions.comp.noteHint')} required value={note} onChange={(e) => setNote(e.target.value)} /> : null}
      <FormActions busy={comp.isPending} submitLabel={t('sessions.comp.submit')} onCancel={onDone} />
    </form>
  );
}

export function SellForm({ organizationId, session, onDone }: { readonly organizationId: string; readonly session: Session; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const sessionId = session.id;
  const sell = useSellSessionTime(organizationId, sessionId);
  const navigate = useNavigate();
  const scope = useConsoleScope();
  // "Sell a pass" (F-SES-17): Sell with the player and this session, so the pass is added to it when it is paid.
  const venueSlug = scope.kind === 'venue' && scope.venue.id === session.venueId ? scope.venue.slug : undefined;
  const [minutes, setMinutes] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const value = Number(minutes);
    if (minutes.trim() === '' || !Number.isInteger(value) || value < 1) {
      setError(t('sessions.sell.required'));
      return;
    }
    setError(undefined);
    sell.mutate(
      { minutes: value },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('sessions.sell.done', { minutes: value }) });
          onDone();
        },
        onError: (failure) => setError(problem.text(failure, { fallback: t('sessions.sell.refused') })),
      },
    );
  }

  return (
    <form className="console-session__form" aria-label={t('sessions.sell.title')} noValidate onSubmit={submit}>
      <MinuteChips value={minutes} onChange={setMinutes} label={t('sessions.sell.minutes')} />
      <TextField label={t('sessions.sell.minutes')} description={t('sessions.sell.minutesHint')} required numeric inputMode="numeric" value={minutes} onChange={(e) => setMinutes(e.target.value)} error={error} />
      <FormActions busy={sell.isPending} submitLabel={t('sessions.sell.submit')} onCancel={onDone} />
      {venueSlug !== undefined && session.playerId !== null ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          icon={<Icon name="card" />}
          onClick={() => void navigate({ to: '/v/$venueSlug/sell', params: { venueSlug }, search: { player: session.playerId ?? undefined, session: session.id } })}
        >
          {t('sessions.sell.pass')}
        </Button>
      ) : null}
    </form>
  );
}

function EndForm({ organizationId, sessionId, booked = false, onDone }: FormProps & { readonly booked?: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const end = useEndSession(organizationId, sessionId);
  const [error, setError] = useState<string | undefined>(undefined);

  return (
    <div className="console-session__form console-session__form--danger" role="group" aria-label={t('sessions.end.title')}>
      <p className="console-session__form-title">{t('sessions.end.title')}</p>
      <p className="console-muted">{booked ? t('sessions.end.bodyBooked') : t('sessions.end.body')}</p>
      {error !== undefined ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
      <div className="console-form__actions">
        <Button
          variant="danger"
          className="clav-button--danger-solid"
          size="sm"
          busy={end.isPending}
          onClick={() => {
            setError(undefined);
            end.mutate(
              {},
              {
                onSuccess: () => {
                  show({ tone: 'ok', title: t('sessions.end.done') });
                  onDone();
                },
                onError: (failure) => setError(problem.text(failure, { fallback: t('sessions.end.refused') })),
              },
            );
          }}
        >
          {t('sessions.end.confirm')}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          {t('sessions.actions.cancel')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Move a running session to another station (F-SES-18, spec/05 §7). Lists the venue's free, enabled
 * stations; the first submit previews the cross-class warnings (passes may not apply, a higher age limit)
 * and, when there are none, moves at once; a second submit confirms the move. The player keeps their time.
 */
function MoveForm({ organizationId, session, onDone }: { readonly organizationId: string; readonly session: Session; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const move = useMoveSession(organizationId, session.id);
  const floor = useFloor(organizationId, session.venueId);
  const [target, setTarget] = useState('');
  const [warnings, setWarnings] = useState<SessionMoveWarning[] | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);

  const candidates = moveCandidates(floor.data?.stations ?? [], session.stationId);
  const confirming = warnings !== null && warnings.length > 0;

  const doConfirm = (): void => {
    move.mutate(
      { stationId: target, confirm: true },
      {
        onSuccess: (result) => {
          if (!result.moved) return;
          show({ tone: 'ok', title: t('sessions.move.moved') });
          onDone();
        },
        onError: (failure) => setError(problem.text(failure, { fallback: t('sessions.move.failed') })),
      },
    );
  };

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (target === '') {
      setError(t('sessions.move.required'));
      return;
    }
    setError(undefined);
    if (confirming) {
      doConfirm();
      return;
    }
    // Preview: show the cross-class warnings, or move at once when there are none (a same-class move).
    move.mutate(
      { stationId: target, confirm: false },
      {
        onSuccess: (result) => (result.warnings.length === 0 ? doConfirm() : setWarnings(result.warnings)),
        onError: (failure) => setError(problem.text(failure, { fallback: t('sessions.move.failed') })),
      },
    );
  }

  return (
    <form className="console-session__form" aria-label={t('sessions.move.title')} noValidate onSubmit={submit}>
      <p className="console-session__form-title">{t('sessions.move.title')}</p>
      {floor.data === undefined && !floor.isError ? (
        <p className="console-muted">{t('sessions.move.loading')}</p>
      ) : candidates.length === 0 ? (
        <Notice tone="info">{t('sessions.move.noFree')}</Notice>
      ) : (
        <>
          <SelectField
            label={t('sessions.move.pick')}
            required
            options={[
              { value: '', label: t('sessions.move.choose') },
              ...candidates.map((station) => ({ value: station.stationId, label: station.name ?? t('sessions.move.stationNumber', { number: station.number }) })),
            ]}
            value={target}
            onValueChange={(value) => {
              setTarget(value);
              setWarnings(null);
              setError(undefined);
            }}
          />
          {warnings !== null && warnings.length > 0 ? (
            <Notice tone="warn" title={t('sessions.move.warningsTitle')}>
              <ul className="console-session__move-warnings">
                {warnings.map((warning) => (
                  <li key={warning.code}>{warning.message}</li>
                ))}
              </ul>
            </Notice>
          ) : null}
          {error !== undefined ? (
            <Notice tone="danger" role="alert">
              {error}
            </Notice>
          ) : null}
          <FormActions busy={move.isPending} submitLabel={confirming ? t('sessions.move.confirm') : t('sessions.move.preview')} onCancel={onDone} />
        </>
      )}
    </form>
  );
}

interface FormProps {
  readonly organizationId: string;
  readonly sessionId: string;
  readonly onDone: () => void;
}

function FormActions({ busy, submitLabel, onCancel }: { readonly busy: boolean; readonly submitLabel: string; readonly onCancel: () => void }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <div className="console-form__actions">
      <Button type="submit" variant="primary" size="sm" busy={busy}>
        {submitLabel}
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
        {t('sessions.actions.cancel')}
      </Button>
    </div>
  );
}

/**
 * One ordered timeline (F-SES-21): the event history, oldest first — staff actions with who did them — then the billed
 * segments with their rate, the titles played with how the station knew them, the gaps it ran offline with the
 * policy applied. The session's journal entries are the Ledger below it (`SessionLedger`), paged on their own.
 */
function Timeline({
  loading,
  segments,
  events,
  plays,
  offline,
  sourceLabel,
  children,
}: {
  readonly loading: boolean;
  readonly segments: readonly SessionSegment[];
  readonly events: readonly SessionEvent[];
  readonly plays: readonly SessionTimelinePlay[];
  readonly offline: readonly SessionTimelineOffline[];
  readonly sourceLabel: (source: string) => string;
  /** The Ledger, last in the section. */
  readonly children?: ReactNode;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Section title={t('sessions.timeline.title')} titleAs="h3">
      {loading ? (
        <Skeleton shape="text" lines={3} />
      ) : events.length === 0 && segments.length === 0 ? (
        <p className="console-muted">{t('sessions.timeline.empty')}</p>
      ) : (
        <>
          <ol className="console-session__events">
            {events.map((event) => (
              <li key={event.id} className="console-session__event">
                <time className="console-num console-subtle" dateTime={event.at}>
                  {formatClock(event.at)}
                </time>
                <span>
                  {describeEvent(event, t)}
                  <span className="console-subtle"> · {actorLabel(event, t)}</span>
                </span>
              </li>
            ))}
          </ol>
          {segments.length > 0 ? (
            <>
              <h4 className="console-session__block-subtitle">{t('sessions.timeline.segmentsTitle')}</h4>
              {/* One row per billed segment: the source, then its time and its charge in their own
                  right-aligned columns so the figures line up down the list. */}
              <ul className="console-session__segments">
                {segments.map((segment) => (
                  <li key={segment.id} className="console-session__segment">
                    <span>
                      {sourceLabel(segment.source)}
                      {segment.rate === null || segment.rate === undefined ? null : (
                        <span className="console-subtle"> · {t('sessions.timeline.segmentRate', { amount: formatMoney(segment.rate.hourlyAmount), unit: formatDuration(segment.rate.billingUnitSeconds) })}</span>
                      )}
                    </span>
                    <span className="console-session__segment-figure clav-figure">{formatDuration(segment.chargedSeconds)}</span>
                    <span className="console-session__segment-figure clav-figure">{formatMoney(segment.chargeAmount)}</span>
                    {/* Play promotions on this segment (F-POS-03): what each took off, under its row. */}
                    {segment.promotions.length > 0 ? (
                      <span className="console-session__segment-promotions">
                        {segment.promotions.map((promotion) => (
                          <span key={promotion.promotionId}>
                            {t('sessions.timeline.segmentPromotion', { name: promotion.name, percent: bpsToPercent(promotion.percentBps), amount: formatMoney(promotion.amount) })}
                          </span>
                        ))}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {plays.length > 0 ? (
            <>
              <h4 className="console-session__block-subtitle">{t('sessions.timeline.playsTitle')}</h4>
              <ul className="console-session__segments">
                {plays.map((play) => (
                  <li key={play.id} className="console-session__segment">
                    <span>
                      {play.name}
                      <span className="console-subtle"> · {t(`sessions.timeline.detectedBy.${play.detectedBy}`, { defaultValue: play.detectedBy })}</span>
                    </span>
                    <span className="console-session__segment-figure clav-figure">{formatClock(play.startedAt)}</span>
                    <span className="console-session__segment-figure clav-figure">{formatDuration(play.seconds)}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {offline.length > 0 ? (
            <>
              <h4 className="console-session__block-subtitle">{t('sessions.timeline.offlineTitle')}</h4>
              <ul className="console-session__segments">
                {offline.map((gap, index) => (
                  <li key={`${gap.kind}:${gap.at}:${index}`} className="console-session__segment">
                    <span>
                      {describeOffline(gap, t)}
                      <span className="console-subtle"> · {t(`sessions.timeline.policy.${gap.policy ?? 'unknown'}`, { defaultValue: gap.policy ?? '' })}</span>
                    </span>
                    <span className="console-session__segment-figure clav-figure">{formatClock(gap.from ?? gap.at)}</span>
                    <span className="console-session__segment-figure clav-figure">{gap.chargedAmount === null ? '—' : formatMoney(gap.chargedAmount)}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </>
      )}
      {children}
    </Section>
  );
}

/** Who a timeline entry is by: the member for a staff action, else the station, the player or the system. */
function actorLabel(event: SessionEvent, t: ConsoleT): string {
  switch (event.actorType) {
    case 'member':
      return t('sessions.timeline.actorStaff');
    case 'device':
      return t('sessions.timeline.actorStation');
    case 'player':
    case 'user':
      return t('sessions.timeline.actorPlayer');
    default:
      return t('sessions.timeline.actorSystem');
  }
}

/** One connectivity gap as a sentence (F-SES-21 rule 5): what kind, and the station's measured time. */
function describeOffline(gap: SessionTimelineOffline, t: ConsoleT): string {
  const minutes = Math.round(gap.measuredSeconds / 60);
  switch (gap.kind) {
    case 'offline_code':
      return t('sessions.timeline.offlineCode', { action: gap.codeAction ?? '', minutes });
    case 'offline_session':
      return t('sessions.timeline.offlineSession', { minutes });
    default:
      return t('sessions.timeline.offlineGap', { minutes, overrun: Math.round((gap.overrunSeconds ?? 0) / 60) });
  }
}

type ConsoleT = ReturnType<typeof useTranslation>['t'];

/** One timeline entry as a sentence, from its kind and detail bag (spec/05 §12). */
function describeEvent(event: SessionEvent, t: ConsoleT): string {
  const detail = event.detail as Record<string, unknown>;
  switch (event.kind) {
    case 'comped': {
      // The reason is a code from the list (F-SES-16), with the note for `other`; older entries carry free text.
      const reason = stringOf(detail['reason']);
      const label = t(`sessions.reason.${reason}`, { defaultValue: reason });
      const note = stringOf(detail['note']);
      return t('sessions.event.comped', { minutes: numberOf(detail['minutes']), reason: note === '' ? label : `${label}: ${note}` });
    }
    case 'sold_time':
      return t('sessions.event.sold_time', { minutes: numberOf(detail['minutes']) });
    case 'pass_added':
      return t('sessions.event.pass_added', { name: stringOf(detail['name']), minutes: Math.round(numberOf(detail['addedSeconds']) / 60) });
    case 'message_sent':
      return t('sessions.event.message_sent', { text: stringOf(detail['text']) });
    case 'warned':
      return t('sessions.event.warned', { minutes: numberOf(detail['minutes']) });
    case 'locked':
      return t(detail['billingPaused'] === true ? 'sessions.event.lockedPaused' : 'sessions.event.locked', {
        reason: t(`sessions.lockReason.${stringOf(detail['reason']) || 'step_away'}`, { defaultValue: '' }),
      });
    case 'unlocked':
      return numberOf(detail['pausedSeconds']) > 0
        ? t('sessions.event.unlockedPaused', { minutes: Math.round(numberOf(detail['pausedSeconds']) / 60) })
        : t('sessions.event.unlocked');
    case 'ended':
      return t('sessions.event.ended', { reason: stringOf(detail['endReason']) });
    case 'grace_started':
      return t('sessions.event.grace_started');
    // "Keep playing with wallet" carries no `via`; the kiosk's "Use my wallet for play time" switch says `via: 'kiosk'`.
    case 'wallet_enabled':
      return t(detail['via'] === 'kiosk' ? 'sessions.event.wallet_enabled_kiosk' : 'sessions.event.wallet_enabled');
    case 'wallet_disabled':
      return t('sessions.event.wallet_disabled');
    case 'offline_settled':
      return numberOf(detail['chargedSeconds']) > 0
        ? t('sessions.event.offline_settled', { minutes: Math.round(numberOf(detail['offlineSeconds']) / 60), charged: Math.round(numberOf(detail['chargedSeconds']) / 60) })
        : t('sessions.event.offline_settled_free', { minutes: Math.round(numberOf(detail['offlineSeconds']) / 60) });
    default:
      return t('sessions.event.started');
  }
}

function numberOf(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function stringOf(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** The local day of an RFC 3339 instant, "2 Oct" — when a pass expires. */
function formatDay(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short' }).format(date);
}

/** The local clock time of an RFC 3339 instant, `HH:MM` — how "play until" and event times read. */
function formatClock(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('en', { hour: '2-digit', minute: '2-digit', hour12: false }).format(date);
}
